import { useEffect } from 'react';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { ThemeButton } from '@shared/ui/buttons';
import { BankSelect, AmountInput } from '@shared/ui/forms/inputs';
import { useForm, useWatch } from 'react-hook-form';
import { FormSum } from '@shared/ui/forms/display';
import Note from '@shared/ui/messages/Note';
import { fetchBankWallets } from '@app/store/slices/bankWalletsSlice';
import { formatBalanceUi, getShortage } from '@utils/balance-helpers';
import { useTransfer } from '@app/api/cards/useTransfer';
import { validateServerErrors } from '@utils/validation';
import { useDispatch, useSelector } from 'react-redux';
import { useExchangeTransfer } from '@app/api/balance/useExchange';
import { useTranslation } from 'react-i18next';
import { currencySymbols, REFERRAL_BANK_ID } from '@utils/static';

export default function TransferDialog({ close, data }) {
  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      transfer_from_bank_id: data?.bank_from ?? null,
      transfer_to_bank_id: data?.bank_id ?? null,
      amount: data?.amount ?? '',
    },
  });

  const { transferBankWallets, referralWithdrawWallets } = useSelector(
    (state) => state.bankWallets,
  );
  const isReferralWithdraw = data?.bank_from === REFERRAL_BANK_ID;
  const wallets = isReferralWithdraw
    ? referralWithdrawWallets
    : transferBankWallets;
  const dispatch = useDispatch();
  const { transfer } = useTransfer();
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(fetchBankWallets());
  }, []);

  async function onSubmit(formData) {
    try {
      const res = await transfer(formData);
      if (res) {
        close();
      }
    } catch (err) {
      validateServerErrors(setError, err);
    }
  }

  const transferFromBankId = useWatch({
    control,
    name: 'transfer_from_bank_id',
  });
  const transferToBankId = useWatch({
    control,
    name: 'transfer_to_bank_id',
  });
  const amountWatch = useWatch({ control, name: 'amount' });

  const transferFromBank = wallets.find(
    (x) => x.bank_id === transferFromBankId,
  );
  const transferToBank = wallets.find((x) => x.bank_id === transferToBankId);

  function replaceBanks() {
    setValue('transfer_from_bank_id', transferToBankId);
    setValue('transfer_to_bank_id', transferFromBankId);
    clearErrors();
  }

  useEffect(() => {
    if (transferToBankId !== null && transferFromBankId === transferToBankId) {
      setValue('transfer_to_bank_id', null);
    }
  }, [transferFromBankId, transferToBankId, setValue]);

  const notEnoughBalance =
    transferFromBank && !isReferralWithdraw
      ? getShortage(
          Number(amountWatch),
          Number(transferFromBank.balance),
          transferFromBank.currency,
        )
      : 0;

  const { exchangeDisplay } = useExchangeTransfer({
    amount: amountWatch,
    fromBank: transferFromBank,
    toBank: transferToBank,
  });

  return (
    <>
      <DialogContent>
        <div className="form transfer-form">
          <Note
            type="info"
            title={t('no_fees')}
            content={t('different_currencies_converted_automatically')}
          />
          <BankSelect
            control={control}
            name="transfer_from_bank_id"
            label={t('from_to')}
            data={wallets}
            displayBalance
            disabled={isReferralWithdraw}
            notEnough={notEnoughBalance}
            error={errors.transfer_from_bank_id}
            isRequired
          />
          <ThemeButton
            appearance="secondary"
            startIcon="transfer-vertical"
            onClick={isReferralWithdraw ? undefined : replaceBanks}
          >
            {!isReferralWithdraw && t('replace')}
          </ThemeButton>
          <BankSelect
            control={control}
            name="transfer_to_bank_id"
            displayBalance
            data={wallets.filter((x) => x.bank_id !== transferFromBankId)}
            disabled={isReferralWithdraw}
            error={errors.transfer_to_bank_id}
            isRequired
          />
          <div className="formGroup-grid">
            <AmountInput
              control={control}
              name="amount"
              label={t('amount')}
              placeholder={
                transferFromBank
                  ? `${t('max')} ${formatBalanceUi(transferFromBank.balance)}`
                  : t('amount')
              }
              max={isReferralWithdraw ? transferFromBank?.balance : undefined}
              disabled={!transferFromBank || !transferToBank}
              error={errors.amount}
              currency={transferFromBank?.currency || 'USD'}
              isRequired
              {...(!isReferralWithdraw &&
                exchangeDisplay?.rate > 0 && {
                  hint: `1 ${currencySymbols[exchangeDisplay.fromCurrency]} = ${exchangeDisplay.rate} ${currencySymbols[exchangeDisplay.toCurrency]}`,
                })}
            />
            <FormSum
              label={t('expected_amount')}
              text={formatBalanceUi(
                exchangeDisplay?.expectedAmount ?? 0,
                exchangeDisplay?.toCurrency ||
                  transferToBank?.currency ||
                  'USD',
                null,
                true,
              )}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <ThemeButton
          minWidth
          startIcon="check"
          loading={isSubmitting}
          disabled={notEnoughBalance > 0}
          onClick={handleSubmit(onSubmit)}
        >
          {t('confirm')}
        </ThemeButton>
      </DialogActions>
    </>
  );
}
